import type { StudioAttention, StudioOverview } from '@/api/types';

/** Most urgent first; the server sorts the same way and the oldest comes first inside a kind. */
export const ATTENTION_ORDER: StudioAttention['kind'][] = [
  'overdue',
  'payment_issue',
  'act_missing',
  'retainer_due',
  'reply',
  'change',
  'secret',
  'stale_client',
  'proposal_silent',
  'change_unanswered',
  'now_outdated',
  'rhythm',
  'onboarding',
  'payer_incomplete',
  'webhook_disabled',
];

/** A client message nobody answered for this long is urgent and shows red. */
export const WAIT_URGENT_HOURS = 24;

/** What the sidebar counts: overdue invoices, payment problems, replies waiting over a day. */
export const isUrgent = (a: StudioAttention) => a.kind === 'overdue' || a.kind === 'payment_issue' || (a.kind === 'reply' && a.waitingHours >= WAIT_URGENT_HOURS);

export const urgentCount = (o?: StudioOverview) => (o ? (o.urgentCount ?? o.attention.filter(isUrgent).length) : undefined);

/** Stable: items of one kind keep their order (the server put the oldest first). */
/** An unknown kind (a newer bff) goes last. */
const rank = (k: string) => {
  const i = ATTENTION_ORDER.indexOf(k as StudioAttention['kind']);
  return i < 0 ? ATTENTION_ORDER.length : i;
};
export const sortAttention = (list: StudioAttention[]) => [...list].sort((a, b) => rank(a.kind) - rank(b.kind));

export type AttentionGroup = { client: string; items: StudioAttention[]; urgent: number };

/** One group per client, in the order the clients first appear: the client with the most urgent item is on top. */
export function groupByClient(list: StudioAttention[]): AttentionGroup[] {
  const groups = new Map<string, AttentionGroup>();
  for (const a of list) {
    const g = groups.get(a.client) ?? { client: a.client, items: [], urgent: 0 };
    g.items.push(a);
    if (isUrgent(a)) g.urgent += 1;
    groups.set(a.client, g);
  }
  return [...groups.values()];
}

/** Hours as a short phrase: «5 ч», «2 дн.». */
export const waitingLabel = (hours: number) => (hours >= 48 ? `${Math.floor(hours / 24)} дн.` : `${hours} ч`);
